import 'katex/dist/katex.min.css';

import { useMemo } from 'react';
import { splitMath } from '@classroom-ai/types';

import { latexToHtml } from '../../utils/math-html';

// ----------------------------------------------------------------------

export type MathTextProps = {
  /** Plain text that may carry `$…$` / `$$…$$` LaTeX spans. */
  text: string | null | undefined;
  /** Render `$$…$$` spans as centered display blocks instead of inline — for
   *  free-typed multi-line text such as student chat messages. The newline on either
   *  side of a display span is dropped so it doesn't add a blank line under `pre-wrap`. */
  display?: boolean;
};

/**
 * Inline renderer for plain (non-Markdown) strings that may contain math — quiz
 * prompts and options, widget items, short labels. Renders text as-is and each math
 * span with KaTeX; returns a fragment, so wrap it in whatever Typography the caller
 * already uses. Text with no math renders exactly as before. Inline by default: `$$…$$`
 * renders at text size, since these strings sit in labels, options and table cells;
 * pass `display` to render it as a block. No Markdown parsing — `3*4*5` stays as typed.
 */
export function MathText({ text, display = false }: MathTextProps) {
  const segments = useMemo(() => {
    const segs = splitMath(text ?? '');
    if (!display) return segs;
    return segs.map((seg, i) => {
      if (seg.kind !== 'text') return seg;
      let { value } = seg;
      const prev = segs[i - 1];
      const next = segs[i + 1];
      if (prev?.kind === 'math' && prev.display) value = value.replace(/^\r?\n/, '');
      if (next?.kind === 'math' && next.display) value = value.replace(/\r?\n$/, '');
      return { ...seg, value };
    });
  }, [text, display]);
  return (
    <>
      {segments.map((seg, i) =>
        seg.kind === 'text' ? (
          seg.value
        ) : (
          <span
             
            key={i}
            className={display && seg.display ? 'math-display' : 'math-inline'}
             
            dangerouslySetInnerHTML={{ __html: latexToHtml(seg.value, display && seg.display) }}
          />
        )
      )}
    </>
  );
}
